import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useHistory } from "react-router-dom";
import api from "../api";

const PageContainer = styled.div`
  min-height: 100vh;
  font-family: "Quicksand", sans-serif;
  padding: 20px;
  background: #f8f6ff;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  color: #8747d4;
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #8747d4;
  font-family: "Quicksand", sans-serif;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  padding: 10px 0;
`;

const CreateButton = styled.button`
  background: #8747d4;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 700;
  font-family: "Quicksand", sans-serif;
  cursor: pointer;
  box-shadow: 0 4px 0 #6535a3;
  margin-bottom: 20px;

  &:active {
    box-shadow: 0 0 0 #6535a3;
  }
`;

const ChallengeCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: transform 0.15s;
  border-left: 5px solid
    ${(props) => {
      if (props.hasWinner) return "#ffd700";
      if (props.isActive) return "#4caf50";
      if (props.isVisible) return "#ff9800";
      return "#ccc";
    }};

  &:hover {
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  flex-wrap: wrap;
  gap: 8px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${(props) => {
    if (props.type === "active") return "#e8f5e9";
    if (props.type === "visible") return "#fff3e0";
    if (props.type === "winner") return "#fff8e1";
    if (props.type === "ended") return "#fce4ec";
    return "#f5f5f5";
  }};
  color: ${(props) => {
    if (props.type === "active") return "#2e7d32";
    if (props.type === "visible") return "#e65100";
    if (props.type === "winner") return "#f57f17";
    if (props.type === "ended") return "#c62828";
    return "#888";
  }};
`;

const CardDate = styled.div`
  font-size: 0.85rem;
  color: #888;
  font-weight: 600;
`;

const CardCourses = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 8px 0;
`;

const CourseTag = styled.span`
  background: #f3eefa;
  color: #8747d4;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  font-size: 0.85rem;
  color: #888;
`;

// Detail view
const DetailCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
`;

const DetailTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 16px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
`;

const ToggleLabel = styled.span`
  font-weight: 600;
  font-size: 0.95rem;
  color: #333;
`;

const ToggleSwitchOuter = styled.div`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
  cursor: pointer;
  border-radius: 28px;
  background-color: ${(props) => (props.active ? "#8747d4" : "#ccc")};
  transition: background-color 0.3s;
  flex-shrink: 0;

  &::before {
    position: absolute;
    content: "";
    height: 22px;
    width: 22px;
    left: ${(props) => (props.active ? "25px" : "3px")};
    bottom: 3px;
    background-color: white;
    transition: left 0.3s;
    border-radius: 50%;
  }
`;

const ActionBtn = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  font-family: "Quicksand", sans-serif;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: transform 0.1s;

  &:active {
    transform: scale(0.97);
  }
`;

const WinnerButton = styled(ActionBtn)`
  background: linear-gradient(135deg, #ffd700, #ffb300);
  color: #4a3600;
  box-shadow: 0 3px 0 #c79100;
  margin-top: 16px;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const DeleteButton = styled(ActionBtn)`
  background: white;
  color: #e53935;
  border: 2px solid #e53935;
  margin-top: 8px;
  font-size: 0.85rem;

  &:hover {
    background: #fef0f0;
  }
`;

const shine = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const WinnerDisplay = styled.div`
  background: linear-gradient(135deg, #ffd700 0%, #fff6a8 50%, #ffd700 100%);
  background-size: 200% auto;
  animation: ${shine} 3s linear infinite;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  margin-top: 12px;
`;

const WinnerName = styled.div`
  font-size: 1.2rem;
  font-weight: 800;
  color: #4a3600;
`;

const ParticipantTable = styled.div`
  margin-top: 16px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 35px 1fr 70px 70px 120px;
  align-items: center;
  padding: 10px 8px;
  border-radius: 8px;
  font-size: 0.85rem;
  background: ${(props) => (props.isHeader ? "transparent" : props.idx % 2 === 0 ? "#fafafa" : "white")};
  font-weight: ${(props) => (props.isHeader ? 700 : 400)};
  color: ${(props) => (props.isHeader ? "#888" : "#333")};

  @media (max-width: 600px) {
    grid-template-columns: 30px 1fr 55px 55px;
    font-size: 0.8rem;
  }
`;

const DateColumn = styled.div`
  @media (max-width: 600px) {
    display: none;
  }
`;

// Create/Edit form
const FormCard = styled(DetailCard)``;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FieldLabel = styled.div`
  font-weight: 700;
  font-size: 0.9rem;
  color: #333;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-family: "Quicksand", sans-serif;
  font-size: 0.95rem;
  box-sizing: border-box;

  &:focus {
    border-color: #8747d4;
    outline: none;
  }
`;

const CourseCheckboxList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
`;

const CourseCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  background: ${(props) => (props.checked ? "#8747d4" : "#f3eefa")};
  color: ${(props) => (props.checked ? "white" : "#8747d4")};
  transition: all 0.2s;
`;

const SaveButton = styled(ActionBtn)`
  background: #8747d4;
  color: white;
  box-shadow: 0 3px 0 #6535a3;
  width: 100%;
  padding: 14px;
  font-size: 1rem;
`;

function formatTime(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function KnowledgeChallengeAdmin() {
  const history = useHistory();
  const [view, setView] = useState("list"); // list | detail | create
  const [challenges, setChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formName, setFormName] = useState("");
  const [formCourses, setFormCourses] = useState([]);
  const [formEndDate, setFormEndDate] = useState("");
  const [formQuestionsPerCourse, setFormQuestionsPerCourse] = useState(10);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [challengesRes, coursesRes] = await Promise.all([
        api.get("/knowledge-challenge/all"),
        api.get("/coursesData"),
      ]);
      setChallenges(challengesRes.data);
      setAllCourses(coursesRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error loading admin data:", error);
      setLoading(false);
    }
  };

  const openDetail = async (challenge) => {
    setSelectedChallenge(challenge);
    setView("detail");
    try {
      const res = await api.get("/knowledge-challenge/stats", {
        params: { challengeId: challenge._id },
      });
      setParticipants(res.data.participants || []);
      if (res.data.winner) {
        setSelectedChallenge((prev) => ({ ...prev, winner: res.data.winner }));
      }
    } catch (error) {
      console.error("Error loading challenge detail:", error);
    }
  };

  const openCreate = () => {
    setFormName("");
    setFormCourses([]);
    setFormEndDate("");
    setFormQuestionsPerCourse(10);
    setView("create");
  };

  const handleToggle = async (field, value) => {
    try {
      const res = await api.put(`/knowledge-challenge/${selectedChallenge._id}`, {
        [field]: value,
      });
      setSelectedChallenge(res.data);
      setChallenges((prev) =>
        prev.map((c) => (c._id === res.data._id ? { ...c, ...res.data } : c))
      );
    } catch (error) {
      console.error("Error toggling:", error);
    }
  };

  const handleCalculateWinner = async () => {
    if (!window.confirm("Calculate and lock in the winner? This action is final.")) return;
    try {
      const res = await api.put(`/knowledge-challenge/calculate-winner/${selectedChallenge._id}`);
      setSelectedChallenge(res.data);
      setChallenges((prev) =>
        prev.map((c) => (c._id === res.data._id ? { ...c, winner: res.data.winner } : c))
      );
    } catch (error) {
      console.error("Error calculating winner:", error);
      alert(error.response?.data?.error || "Failed to calculate winner");
    }
  };

  const handleCreate = async () => {
    if (formCourses.length === 0) {
      alert("Please select at least one course");
      return;
    }
    if (!formEndDate) {
      alert("Please set an end date");
      return;
    }
    setSaving(true);
    try {
      await api.post("/knowledge-challenge", {
        name: formName || "Knowledge Challenge",
        courses: formCourses,
        endDate: new Date(formEndDate).toISOString(),
        questionsPerCourse: formQuestionsPerCourse,
        isActive: false,
        isVisible: false,
      });
      await loadData();
      setView("list");
    } catch (error) {
      console.error("Error creating challenge:", error);
      alert("Failed to create challenge");
    }
    setSaving(false);
  };

  const toggleCourseSelection = (courseTitle) => {
    setFormCourses((prev) =>
      prev.includes(courseTitle) ? prev.filter((c) => c !== courseTitle) : [...prev, courseTitle]
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <PageTitle>Loading...</PageTitle>
      </PageContainer>
    );
  }

  // CREATE VIEW
  if (view === "create") {
    return (
      <PageContainer>
        <ContentWrapper>
          <BackButton onClick={() => setView("list")}>← Back to List</BackButton>
          <PageTitle>Create New Challenge</PageTitle>

          <FormCard>
            <FormGroup>
              <FieldLabel>Challenge Name</FieldLabel>
              <Input
                type="text"
                placeholder="e.g. Knowledge Challenge"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <FieldLabel>Select Courses</FieldLabel>
              <CourseCheckboxList>
                {allCourses.map((course) => {
                  const title = course.courseTitle;
                  const isChecked = formCourses.includes(title);
                  return (
                    <CourseCheckbox
                      key={title}
                      checked={isChecked}
                      onClick={() => toggleCourseSelection(title)}
                    >
                      {title}
                    </CourseCheckbox>
                  );
                })}
              </CourseCheckboxList>
            </FormGroup>

            <FormGroup>
              <FieldLabel>End Date</FieldLabel>
              <Input
                type="datetime-local"
                value={formEndDate}
                onChange={(e) => setFormEndDate(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <FieldLabel>Questions Per Course</FieldLabel>
              <Input
                type="number"
                min="1"
                max="50"
                value={formQuestionsPerCourse}
                onChange={(e) => setFormQuestionsPerCourse(parseInt(e.target.value) || 10)}
              />
            </FormGroup>

            <SaveButton onClick={handleCreate} disabled={saving}>
              {saving ? "Creating..." : "Create Challenge"}
            </SaveButton>
          </FormCard>
        </ContentWrapper>
      </PageContainer>
    );
  }

  // DETAIL VIEW
  if (view === "detail" && selectedChallenge) {
    const isEnded = selectedChallenge.endDate && new Date(selectedChallenge.endDate) < new Date();

    return (
      <PageContainer>
        <ContentWrapper>
          <BackButton onClick={() => { setView("list"); setParticipants([]); }}>
            ← Back to List
          </BackButton>
          <PageTitle>{selectedChallenge.name || "Knowledge Challenge"}</PageTitle>

          <DetailCard>
            <DetailTitle>Configuration</DetailTitle>
            <CardCourses>
              {selectedChallenge.courses?.map((c) => (
                <CourseTag key={c}>{c}</CourseTag>
              ))}
            </CardCourses>
            <CardDate style={{ margin: "8px 0" }}>
              End Date:{" "}
              {selectedChallenge.endDate
                ? new Date(selectedChallenge.endDate).toLocaleString()
                : "Not set"}
            </CardDate>
            <CardDate>
              Questions per course: {selectedChallenge.questionsPerCourse || 10}
            </CardDate>
            <CardDate style={{ marginTop: 4 }}>
              Created: {new Date(selectedChallenge.createdAt).toLocaleDateString()}
            </CardDate>

            <div style={{ marginTop: 20 }}>
              <ToggleRow>
                <ToggleLabel>Active (users can take it)</ToggleLabel>
                <ToggleSwitchOuter
                  active={selectedChallenge.isActive}
                  onClick={() => handleToggle("isActive", !selectedChallenge.isActive)}
                />
              </ToggleRow>
              <ToggleRow>
                <ToggleLabel>Visible (button on home screen)</ToggleLabel>
                <ToggleSwitchOuter
                  active={selectedChallenge.isVisible}
                  onClick={() => handleToggle("isVisible", !selectedChallenge.isVisible)}
                />
              </ToggleRow>
            </div>
          </DetailCard>

          <DetailCard>
            <DetailTitle>Winner</DetailTitle>
            {selectedChallenge.winner ? (
              <WinnerDisplay>
                <div style={{ fontSize: "0.8rem", color: "#806000", fontWeight: 700 }}>
                  WINNER
                </div>
                <WinnerName>
                  🏆 {selectedChallenge.winner.givenName}{" "}
                  {selectedChallenge.winner.familyName}
                </WinnerName>
                <div style={{ color: "#806000", fontWeight: 600, marginTop: 6 }}>
                  Score: {selectedChallenge.winner.score} | Time:{" "}
                  {formatTime(selectedChallenge.winner.timeTaken)}
                </div>
              </WinnerDisplay>
            ) : (
              <>
                <div style={{ color: "#888", marginBottom: 10 }}>
                  No winner calculated yet.
                </div>
                <WinnerButton
                  onClick={handleCalculateWinner}
                  disabled={participants.length === 0}
                >
                  🏆 Calculate Winner
                </WinnerButton>
                {participants.length === 0 && (
                  <div style={{ color: "#e53935", fontSize: "0.8rem", marginTop: 6 }}>
                    No participants yet
                  </div>
                )}
              </>
            )}
          </DetailCard>

          <DetailCard>
            <DetailTitle>Participants ({participants.length})</DetailTitle>
            {participants.length > 0 ? (
              <ParticipantTable>
                <TableRow isHeader>
                  <div>#</div>
                  <div>Name</div>
                  <div style={{ textAlign: "center" }}>Score</div>
                  <div style={{ textAlign: "center" }}>Time</div>
                  <DateColumn>Date</DateColumn>
                </TableRow>
                {participants.map((p, idx) => (
                  <TableRow key={p._id} idx={idx}>
                    <div style={{ fontWeight: 700 }}>{idx + 1}</div>
                    <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.givenName} {p.familyName}
                    </div>
                    <div style={{ textAlign: "center", fontWeight: 700, color: "#8747d4" }}>
                      {p.score}/{p.totalQuestions}
                    </div>
                    <div style={{ textAlign: "center" }}>{formatTime(p.timeTaken)}</div>
                    <DateColumn style={{ fontSize: "0.75rem", color: "#888" }}>
                      {new Date(p.completedAt).toLocaleString()}
                    </DateColumn>
                  </TableRow>
                ))}
              </ParticipantTable>
            ) : (
              <div style={{ color: "#888" }}>No participants yet.</div>
            )}
          </DetailCard>
        </ContentWrapper>
      </PageContainer>
    );
  }

  // LIST VIEW
  return (
    <PageContainer>
      <ContentWrapper>
        <BackButton onClick={() => history.push("/profile")}>← Back to Profile</BackButton>
        <PageTitle>Knowledge Challenges</PageTitle>
        <CreateButton onClick={openCreate}>+ Create New Challenge</CreateButton>

        {challenges.length === 0 ? (
          <div style={{ color: "#888", textAlign: "center", marginTop: 40 }}>
            No challenges created yet.
          </div>
        ) : (
          challenges.map((c) => {
            const isEnded = c.endDate && new Date(c.endDate) < new Date();
            return (
              <ChallengeCard
                key={c._id}
                isActive={c.isActive}
                isVisible={c.isVisible}
                hasWinner={!!c.winner}
                onClick={() => openDetail(c)}
              >
                <CardHeader>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {c.isActive && <StatusBadge type="active">Active</StatusBadge>}
                    {c.isVisible && <StatusBadge type="visible">Visible</StatusBadge>}
                    {c.winner && <StatusBadge type="winner">Winner Decided</StatusBadge>}
                    {isEnded && !c.winner && <StatusBadge type="ended">Ended</StatusBadge>}
                    {!c.isActive && !c.isVisible && !isEnded && (
                      <StatusBadge>Draft</StatusBadge>
                    )}
                  </div>
                  <CardDate>
                    Ends: {c.endDate ? new Date(c.endDate).toLocaleDateString() : "—"}
                  </CardDate>
                </CardHeader>
                <div style={{ fontWeight: 700, fontSize: "1.1rem", color: "#333", margin: "8px 0 4px" }}>
                  {c.name || "Knowledge Challenge"}
                </div>
                <CardCourses>
                  {c.courses?.map((course) => (
                    <CourseTag key={course}>{course}</CourseTag>
                  ))}
                </CardCourses>
                <CardFooter>
                  <span>{c.participantCount || 0} participant(s)</span>
                  <span>{c.questionsPerCourse || 10} questions/course</span>
                </CardFooter>
              </ChallengeCard>
            );
          })
        )}
      </ContentWrapper>
    </PageContainer>
  );
}

export default KnowledgeChallengeAdmin;
